import React from 'react';
import { calculateTotalGSTBreakdown, formatGSTBreakdownForDisplay } from '../utils/gstUtils';

/**
 * GST Breakdown Display Component
 * Shows CGST/SGST or IGST breakdown based on state logic
 */
const GSTBreakdown = ({
  items = [],
  buyerGST = '',
  buyerStateName = '',
  companySettings = null,
  className = '',
  showTitle = true,
  titleClassName = 'text-sm font-medium text-slate-600 mb-2'
}) => {
  // Calculate GST breakdown
  const gstBreakdown = calculateTotalGSTBreakdown(items, buyerGST, companySettings, buyerStateName);
  const displayData = formatGSTBreakdownForDisplay(gstBreakdown);

  // If no GST, don't render anything
  if (gstBreakdown.totalGST === 0) {
    return null;
  }

  return (
    <div className={`gst-breakdown ${className}`}>
      {showTitle && (
        <div className={titleClassName}>
          GST Breakdown {gstBreakdown.isSameState ? '(Same State)' : '(Inter-State)'}
        </div>
      )}

      <div className="space-y-1">
        {displayData.lines.map((line, index) => (
          <div key={index} className="flex justify-between items-center">
            <span className="text-sm text-slate-700">{line.label}:</span>
            <span className="text-sm font-medium text-slate-900">{line.formatted}</span>
          </div>
        ))}

        {/* Total GST line with emphasis */}
        <div className="flex justify-between items-center pt-1 border-t border-slate-200">
          <span className="text-sm font-semibold text-slate-800">{displayData.total.label}:</span>
          <span className="text-sm font-bold text-slate-900">{displayData.total.formatted}</span>
        </div>
      </div>

      {/* Debug info (only in development) */}
      {process.env.NODE_ENV === 'development' && (
        <div className="mt-2 p-2 bg-gray-100 rounded text-xs text-gray-600">
          <div>Buyer State: {gstBreakdown.buyerStateCode || 'N/A'}</div>
          <div>Seller State: {gstBreakdown.sellerStateCode || 'N/A'}</div>
          <div>GST Type: {gstBreakdown.type}</div>
        </div>
      )}
    </div>
  );
};

/**
 * Compact GST Breakdown for tables and small spaces
 */
export const CompactGSTBreakdown = ({
  items = [],
  buyerGST = '',
  companySettings = null,
  className = ''
}) => {
  const gstBreakdown = calculateTotalGSTBreakdown(items, buyerGST, companySettings);
  const displayData = formatGSTBreakdownForDisplay(gstBreakdown);

  if (gstBreakdown.totalGST === 0) {
    return <span className="text-slate-500">No GST</span>;
  }

  return (
    <div className={`compact-gst-breakdown ${className}`}>
      {displayData.lines.map((line, index) => (
        <div key={index} className="text-xs text-slate-600">
          {line.label}: {line.formatted}
        </div>
      ))}
    </div>
  );
};

/**
 * GST Summary for totals sections
 */
export const GSTSummary = ({
  items = [],
  buyerGST = '',
  companySettings = null,
  subtotal = 0,
  amountReceived = 0,
  showOutstanding = false,
  className = ''
}) => {
  const gstBreakdown = calculateTotalGSTBreakdown(items, buyerGST, companySettings);
  const displayData = formatGSTBreakdownForDisplay(gstBreakdown);
  const grandTotal = subtotal + gstBreakdown.totalGST;
  const outstanding = grandTotal - amountReceived;

  return (
    <div className={`gst-summary ${className}`}>
      {/* Subtotal */}
      <div className="flex justify-between py-2" style={{ borderBottom: '1px solid black' }}>
        <span className="font-medium">Subtotal:</span>
        <span className="font-medium">₹{subtotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
      </div>

      {/* GST Breakdown */}
      {displayData.lines.map((line, index) => (
        <div key={index} className="flex justify-between py-1">
          <span className="text-slate-700">{line.label}:</span>
          <span className="text-slate-900">{line.formatted}</span>
        </div>
      ))}

      {/* Total GST */}
      <div className="flex justify-between py-2" style={{ borderBottom: '1px solid black' }}>
        <span className="font-medium">{displayData.total.label}:</span>
        <span className="font-medium">{displayData.total.formatted}</span>
      </div>

      {/* Grand Total */}
      <div className="flex justify-between py-2 font-semibold" style={{ borderBottom: '1px solid black' }}>
        <span>Grand Total:</span>
        <span>₹{grandTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
      </div>

      {/* Amount Received and Outstanding (if requested) */}
      {showOutstanding && (
        <>
          <div className="flex justify-between py-2 text-green-600">
            <span>Amount Received:</span>
            <span>₹{amountReceived.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
          </div>
          <div className="flex justify-between py-2 font-bold text-lg">
            <span>Total Outstanding:</span>
            <span>₹{outstanding.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
          </div>
        </>
      )}
    </div>
  );
};

export default GSTBreakdown;
